import { Link } from "react-router-dom"
import {
  FileText, IdCard, GraduationCap, Award, MapPin, Calendar, FileCheck,
  ClipboardCheck, ArrowRight, CheckCircle2, AlertCircle, Info,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { Alert } from "../../components/ui/Alert"

const requiredDocuments = [
  { icon: IdCard, title: "Birth Certificate / Declaration of Age", text: "An officially recognised birth certificate or a sworn declaration of age from a High Court. Must be legible and not expired." },
  { icon: FileText, title: "O-Level Result(s)", text: "Original and photocopy of WAEC, NECO, NBAIS or equivalent. At least 5 credits including English and Mathematics in not more than two sittings." },
  { icon: Award, title: "JAMB UTME Result Slip", text: "Printout of the current year's JAMB UTME result showing the candidate's score and the chosen course of study at YMS COE." },
  { icon: GraduationCap, title: "Testimonial from Last School", text: "A testimonial or letter of recommendation from the principal of the secondary school last attended, characterised by the school stamp." },
  { icon: FileCheck, title: "Passport Photograph", text: "Six (6) recent coloured passport photographs with red background, taken within the last three months. Upload one with the application." },
  { icon: MapPin, title: "Local Government Indigene Letter", text: "A letter of identification from the candidate's Local Government Area or a State of Origin certificate, signed and stamped." },
]

const applicationSteps = [
  { num: 1, title: "Create an Account", text: "Visit the Student Portal registration page and create an account using a valid email address and phone number. Verify your email to activate the account." },
  { num: 2, title: "Complete the Application Form", text: "Log in and fill out the multi-section application form with personal details, JAMB information, O-Level results, guardian and next-of-kin information, and medical particulars." },
  { num: 3, title: "Upload Required Documents", text: "Upload scanned copies of your birth certificate, O-Level results, JAMB slip, testimonial, passport photograph and indigene letter. Each document must meet the specified size and format." },
  { num: 4, title: "Pay the Application Fee", text: "Pay the non-refundable application fee online using Paystack, Flutterwave or Monnify. The system generates a unique reference for every transaction." },
  { num: 5, title: "Submit and Print", text: "Review all sections carefully before submitting. After submission, print the application acknowledgment slip — you will need it for screening and verification." },
  { num: 6, title: "Attend Screening", text: "Shortlisted candidates are invited for physical screening. Bring all original documents and the printed acknowledgment slip. Admission list is published on the portal thereafter." },
]

const importantDates = [
  { event: "Application Portal Opens", date: "January 15, 2025", icon: Calendar, tone: "text-brand-700 dark:text-brand-400" },
  { event: "Application Deadline", date: "September 30, 2025", icon: Calendar, tone: "text-gold-700 dark:text-gold-400" },
  { event: "Screening Exercise", date: "October 15 – 17, 2025", icon: ClipboardCheck, tone: "text-brand-700 dark:text-brand-400" },
  { event: "Admission List Released", date: "October 30, 2025", icon: Award, tone: "text-gold-700 dark:text-gold-400" },
  { event: "Acceptance Fee Payment", date: "November 1 – 21, 2025", icon: FileCheck, tone: "text-brand-700 dark:text-brand-400" },
  { event: "Resumption & Registration", date: "December 1, 2025", icon: GraduationCap, tone: "text-gold-700 dark:text-gold-400" },
]

export default function AdmissionRequirements() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Admission" }]}
        subtitle="Admission Requirements"
        title="Your Pathway to Becoming a YMS COE Student"
        description="Find comprehensive entry requirements for the NCE, Pre-NCE and direct entry candidates, the documents you need to submit, and a step-by-step guide through the application process."
      />

      {/* General Requirements */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge variant="success" className="mb-3">General Requirements</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                Who Can Apply?
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                Admission into the Nigeria Certificate in Education (NCE) programme at Yusuf Maitama Sule College of Education is open to candidates who satisfy both the academic and character requirements prescribed by the National Commission for Colleges of Education (NCCE) and the College's Admissions Committee.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed">
                Candidates must demonstrate academic readiness, good character and a genuine interest in the teaching profession. Selection is competitive and based on a combination of O-Level performance, UTME score and performance at the screening exercise.
              </p>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  Minimum Academic Requirements
                </h3>
                <ul className="space-y-3 text-sm text-ink-700 dark:text-ink-300">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> At least five (5) O-Level credit passes including English Language and Mathematics.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> Credits obtained in not more than two sittings from WAEC, NECO or NBAIS.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> Three other credits must be relevant to the chosen teaching subject combination.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> Minimum UTME score as prescribed for Colleges of Education in the year of application.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> Candidate must be at least 16 years of age at the time of application.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* NCE / Pre-NCE specific */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-brand-700 grid place-items-center mb-4">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">NCE Direct Entry</h3>
                <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                  For candidates who already hold the NCE, a relevant National Diploma (ND at upper credit), a Diploma in Education or a Grade II Teacher's Certificate with credit passes. Direct entry candidates may be admitted into NCE 2 or exempted from select courses, subject to the Academic Board's evaluation.
                </p>
                <ul className="space-y-2 text-sm text-ink-700 dark:text-ink-300">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> NCE in a relevant discipline from a recognised institution</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> ND at upper credit in a related field</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" /> Original transcripts and certificate of qualification</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gold-600 grid place-items-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">Pre-NCE / Remedial</h3>
                <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                  For candidates who do not yet meet direct entry requirements. The Pre-NCE is a one-year intensive remedial programme that prepares students for progression into the NCE. Successful candidates progress directly without reapplying.
                </p>
                <ul className="space-y-2 text-sm text-ink-700 dark:text-ink-300">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-gold-600 dark:text-gold-400 mt-0.5 shrink-0" /> At least three (3) O-Level credits including English or Mathematics</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-gold-600 dark:text-gold-400 mt-0.5 shrink-0" /> Minimum age of 15 years</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-gold-600 dark:text-gold-400 mt-0.5 shrink-0" /> Genuine interest in pursuing a teaching career</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Document Checklist</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Required Documents</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              All applicants must submit the following documents. Originals will be inspected during physical screening.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {requiredDocuments.map((d, i) => (
              <Card key={i} className="card-lift border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center shrink-0">
                      <d.icon className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-sm text-ink-900 dark:text-ink-100 mb-1.5">{d.title}</h3>
                      <p className="text-xs text-ink-600 dark:text-ink-400 leading-relaxed">{d.text}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process Timeline */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Application Process</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold">6 Steps to Apply</h2>
            <p className="text-white/70 mt-3 max-w-2xl mx-auto">From account creation to screening — here is what to expect at each stage.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {applicationSteps.map(s => (
              <div key={s.num} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors relative">
                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-gold-500 text-ink-950 grid place-items-center font-display font-bold text-lg shadow-lg">
                  {s.num}
                </div>
                <h3 className="font-display font-bold text-lg mb-2 mt-2">{s.title}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Dates */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="success" className="mb-3">2025/2026 Session</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Important Dates</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">Mark these dates on your calendar so you don't miss any deadline.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {importantDates.map(d => (
              <Card key={d.event} className="card-lift border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center shrink-0">
                      <d.icon className={`w-6 h-6 ${d.tone}`} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-500 dark:text-ink-400 mb-1">{d.event}</div>
                      <div className="font-display font-bold text-ink-900 dark:text-ink-100">{d.date}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Alert variant="warning" className="mt-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                <strong className="block mb-1">Note on Deadlines</strong>
                All deadlines are strictly enforced. Late applications, incomplete forms or non-payment of fees by the deadline will result in disqualification. Always submit at least 48 hours before the deadline to avoid last-minute issues.
              </div>
            </div>
          </Alert>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-800 to-brand-950 text-white p-10 md:p-14">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-gold-500/10 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-300 mb-3">
                  <Info className="w-4 h-4" /> Ready to Begin?
                </div>
                <h2 className="font-display text-3xl font-bold mb-3">Start Your Application Today</h2>
                <p className="text-white/80">Applications for the 2025/2026 academic session are now open. Create your Student Portal account and begin your journey toward a rewarding career in education.</p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/portal/register">
                  <Button size="lg" className="w-full bg-gold-500 hover:bg-gold-600 text-ink-950">
                    Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full bg-white/5 border-white/30 text-white hover:bg-white/10">
                    Ask a Question
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
